import {makeAutoObservable} from 'mobx';
import {ACTIVE_ANNOUNCEMENT, type Announcement} from 'announcements';
import {announcementSeenRequest} from 'requests';
import type {RootStore} from 'stores/rootStore';

export const ANNOUNCEMENT_MODAL_ID = 'announcement';
/** How long a scan's result stays on screen alone before the announcement comes up. */
export const ANNOUNCEMENT_DELAY_MS = 5000;

/** Shows the release's announcement (announcements/) to whoever it is for, once per account. */
export class AnnouncementStore {
    root: RootStore;
    private active: Announcement | null;
    /** Shown on this page. Kept apart from the account's list: a /users/me already in flight
     *  when it was shown would land without it and bring it back. */
    private shown = false;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private armedAt = 0;

    constructor(root: RootStore, active: Announcement | null = ACTIVE_ANNOUNCEMENT) {
        this.root = root;
        this.active = active;
        // Config, not state: observing it would wrap `audience`/`onCta` in actions.
        makeAutoObservable<AnnouncementStore, 'active' | 'timer' | 'armedAt'>(this, {root: false, active: false, timer: false, armedAt: false});
    }

    /** The announcement this account is still to be shown, if any. */
    get pending(): Announcement | null {
        const a = this.active;
        const {user, widgetMode, reportViewMode} = this.root;
        if (!a || this.shown || widgetMode || reportViewMode || user.isAnonymous) return null;
        if (user.user?.seen_announcements?.includes(a.id)) return null;
        return a.audience(this.root) ? a : null;
    }

    /** A scan's result has landed (any check, text or batch). The delay restarts with each
     *  result, so it counts from the last one in. */
    scanLanded() {
        if (!this.pending) return;
        this.cancel();
        this.armedAt = Date.now();
        this.timer = setTimeout(() => {
            this.timer = null;
            this.show();
        }, ANNOUNCEMENT_DELAY_MS);
    }

    /** The scan page is gone: the modal is registered there, and is not to come up elsewhere. */
    cancel() {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
    }

    private show() {
        const a = this.pending;
        const {modal, socialMedia, input, batchFile, layout} = this.root;
        // Something else has the user now — another popup (the feedback form, the paywall,
        // the share form), a new scan, a batch whose other checks are still coming in, or the
        // Humanizer itself: a later result will try again.
        if (!a || modal.isModalOpen() || socialMedia.showSocialMediaForm || input.lastRunTime > this.armedAt
            || batchFile.isRunning || layout.humanizerOpen) return;
        this.shown = true;
        announcementSeenRequest(a.id).catch(console.error);
        modal.showModal(ANNOUNCEMENT_MODAL_ID, {announcement: a});
    }
}
